import type { Dispatch } from '@reduxjs/toolkit';

import {
  getAudioOverviewAudio,
  saveAudioOverviewProgress,
  toAudioOverviewError,
} from '@/services/audioOverviews';
import {
  audioOverviewProgressSaved,
  audioOverviewReplayStarted,
  audioOverviewUpgradePromptOpened,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { AudioOverviewSummary } from '@/types/AudioOverview';
import {
  getAudioOverviewPlayer,
  type AudioOverviewPlayer,
  type AudioOverviewProgressHandler,
  type AudioOverviewTrack,
} from './player/audioOverviewPlayer';
import {
  captureAudioOverviewCompleted,
  captureAudioOverviewPlayed,
  captureAudioOverviewUpgradePrompted,
  type AudioOverviewUpgradeSource,
} from './audioOverviewAnalytics';
import { describeAudioOverviewFormat } from './audioOverviewCopy';

/**
 * Wires the Audio Overview API into PR 1's playback engine: the track's
 * signed-URL source, the resume-position saves and the paywall.
 */

export const AUDIO_OVERVIEW_FALLBACK_TITLE = 'Audio Overview';

/** Within this many seconds of the end, Play starts over instead of resuming. */
const RESTART_WITHIN_SEC = 5;

export const promptAudioOverviewUpgrade = (
  dispatch: Dispatch,
  source: AudioOverviewUpgradeSource,
  context: {
    sourceKind?: AudioOverviewSummary['source'];
    articleId?: string | null;
  } = {}
): void => {
  dispatch(audioOverviewUpgradePromptOpened(context.sourceKind));
  captureAudioOverviewUpgradePrompted(source, context);
};

/**
 * Finished: listened to the end (`completedAt`) or saved within a few seconds
 * of it. Such an episode offers Replay, from the start.
 */
export const isAudioOverviewCompleted = (
  overview: Pick<
    AudioOverviewSummary,
    'positionSec' | 'durationSec' | 'completedAt'
  >
): boolean => {
  if (overview.completedAt) return true;
  const position = overview.positionSec ?? 0;
  return (
    position > 0 &&
    overview.durationSec !== null &&
    position >= overview.durationSec - RESTART_WITHIN_SEC
  );
};

/** Where Play should start: the saved position, unless it was finished. */
export const audioOverviewStartAt = (
  overview: Pick<
    AudioOverviewSummary,
    'positionSec' | 'durationSec' | 'completedAt'
  >
): number => {
  if (isAudioOverviewCompleted(overview)) return 0;
  return Math.max(0, overview.positionSec ?? 0);
};

const subtitleFor = (overview: AudioOverviewSummary): string => {
  const titles = overview.lectures
    .map((lecture) => lecture.title)
    .filter(Boolean);

  const source =
    overview.source === 'article'
      ? 'Library article'
      : titles.length === 0
        ? ''
        : titles.length === 1
          ? titles[0]
          : `${titles[0]} + ${titles.length - 1} more`;

  const format = describeAudioOverviewFormat(overview);
  return source ? `${format} · ${source}` : format;
};

export const toAudioOverviewTrack = (
  overview: AudioOverviewSummary,
  { onUpgradeRequired }: { onUpgradeRequired: () => void }
): AudioOverviewTrack => ({
  id: overview.id,
  title:
    (overview.source === 'article' ? overview.articleTitle : null) ??
    overview.title ??
    AUDIO_OVERVIEW_FALLBACK_TITLE,
  subtitle: subtitleFor(overview),
  sourceKind: overview.source,
  articleId: overview.articleId,
  durationSec: overview.durationSec ?? undefined,
  getSource: async () => {
    try {
      const { url, expiresAt } = await getAudioOverviewAudio(overview.id);
      return { url, expiresAt };
    } catch (error) {
      if (
        toAudioOverviewError(error).code === 'AUDIO_OVERVIEW_UPGRADE_REQUIRED'
      ) {
        onUpgradeRequired();
      }
      throw error;
    }
  },
});

interface PlayAudioOverviewDeps {
  dispatch: Dispatch;
  player?: AudioOverviewPlayer;
}

/**
 * Plays an overview from a tap (so iOS lets the element start). A locked one
 * opens the paywall instead. Re-tapping the episode that is already loaded
 * just resumes it rather than jumping to the last saved position.
 *
 * A finished episode (or the loaded one after it ended) is replayed from the
 * start through a fresh load, so `getSource()` re-checks access instead of
 * the engine reusing the URL it already has.
 */
export const playAudioOverview = (
  overview: AudioOverviewSummary,
  { dispatch, player = getAudioOverviewPlayer() }: PlayAudioOverviewDeps
): void => {
  if (overview.locked) {
    promptAudioOverviewUpgrade(dispatch, 'locked_overview', {
      sourceKind: overview.source,
      articleId: overview.articleId,
    });

    return;
  }
  if (overview.status !== 'READY') return;

  const { track: loaded, status } = player.getState();
  const isLoaded = loaded?.id === overview.id;

  const isReplay =
    (isLoaded && status === 'ended') ||
    (!isLoaded && isAudioOverviewCompleted(overview));

  if (isLoaded && !isReplay) {
    player.play();
    return;
  }

  if (isLoaded) player.close();
  if (isReplay) dispatch(audioOverviewReplayStarted(overview.id));

  const startAt = isReplay ? 0 : audioOverviewStartAt(overview);

  const track = toAudioOverviewTrack(overview, {
    // The plan lapsed mid-session: stop, unload and show the paywall.
    onUpgradeRequired: () => {
      player.close();

      promptAudioOverviewUpgrade(dispatch, 'playback_rejected', {
        sourceKind: overview.source,
        articleId: overview.articleId,
      });
    },
  });

  player.load(track, { autoplay: true, startAt });

  captureAudioOverviewPlayed(overview, {
    resumed: startAt > 0,
    replay: isReplay,
  });
};

interface ProgressHandlerDeps {
  dispatch: Dispatch;
  getOverview: (id: string) => AudioOverviewSummary | undefined;
  /** A save came back 403 UPGRADE_REQUIRED: the plan lapsed mid-episode. */
  onUpgradeRequired?: (trackId: string) => void;
  save?: typeof saveAudioOverviewProgress;
}

/**
 * The engine's progress callback: saves the position (fire-and-forget), keeps
 * the stored copy current for Resume, reports each episode's first completion
 * this session, and hands a 403 UPGRADE_REQUIRED to `onUpgradeRequired`.
 */
export const createAudioOverviewProgressHandler = ({
  dispatch,
  getOverview,
  onUpgradeRequired,
  save = saveAudioOverviewProgress,
}: ProgressHandlerDeps): AudioOverviewProgressHandler => {
  const completedThisSession = new Set<string>();

  return ({ trackId, positionSec, completed }) => {
    void save(trackId, { positionSec, completed }).then((result) => {
      if (result === 'upgrade-required') onUpgradeRequired?.(trackId);
    });

    dispatch(
      audioOverviewProgressSaved({ id: trackId, positionSec, completed })
    );

    if (!completed || completedThisSession.has(trackId)) return;
    completedThisSession.add(trackId);
    const overview = getOverview(trackId);
    if (overview) captureAudioOverviewCompleted(overview);
  };
};
